// PlatformTip — мини-попап платформы/стадии над строкой токена (Discover /
// Search / Trenches; axiom-манера «Bonding: 0%»). Текст — цветом бренда
// источника; рамка НЕЙТРАЛЬНАЯ (фидбек: «обводка не должна быть под цвет»).
// Честность: нет ни launchpad-источника, ни dex — попапа нет; версии DEX
// берутся из source-backed dex_label, а без него — только из надёжных
// ключей (никогда не выдумываются).
//
// Позиционирование: position:fixed по центру верхней кромки строки-якоря
// (половина попапа над кромкой) — попап НЕ клипается overflow-скроллом и
// у верхней строки ложится поверх шапки колонки, как у всех (фидбек).
// Слушает mouseenter/mouseleave якоря; прячется на любой скролл.

import { type RefObject, useEffect, useState } from 'react'
import { dexDisplayLabel, poolDexDisplayLabel } from '../../lib/dexLabels'
import styles from './PlatformTip.module.css'
import { figs } from './fig'
import { GOLD_STOPS, SourceLogo, sourceColor } from './icons/SourceLogo'

export interface PlatformTipToken {
  launchpad_source?: string | null
  launchpad_status?: string | null
  bonding_progress_pct?: number | null
  dex?: string | null
  /** source-backed точный лейбл площадки («DeDust Classic»), когда API отдал */
  dex_label?: string | null
}

// Точный лейбл площадки: source-backed dex_label из API («DeDust Classic» /
// «STON.fi v2»; компакт: без слова CPMM). Фолбэк без dex_label: у STON.fi
// версия из ключа надёжна (v1/v2 — разные роутеры), у DeDust родовой ключ
// dedust_v2 висит и на Classic-пулах — версию не выдумываем.
function tipDexLabel(t: PlatformTipToken, dex: string): string {
  if (t.dex_label) return t.dex_label.replace(' CPMM', '')
  if (dex.startsWith('dedust')) return 'DeDust'
  return poolDexDisplayLabel(dex)
}

/** What the tip says, in its parts: whose mark, the name, the stage, its figure. */
export interface PlatformTipParts {
  /** the source whose mark is drawn */
  source: string
  name: string
  stage: 'Bonding' | 'Migrated' | null
  /** the bonding progress, when it is known */
  value: string | null
  /** the brand's colour (gold for a migrated token) */
  color: string
}

export function platformTipParts(t: PlatformTipToken): PlatformTipParts | null {
  if (t.launchpad_source) {
    const name = dexDisplayLabel(t.launchpad_source)
    if (t.launchpad_status === 'migrated') {
      const dexPart = t.dex ? ` → ${tipDexLabel(t, t.dex)}` : ''
      return {
        source: t.launchpad_source,
        name: `${name}${dexPart}`,
        stage: 'Migrated',
        value: null,
        color: GOLD_STOPS[1],
      }
    }
    const pct = t.bonding_progress_pct
    return {
      source: t.launchpad_source,
      name,
      stage: 'Bonding',
      value: pct != null && Number.isFinite(pct) ? `${Math.round(pct)}%` : null,
      color: sourceColor(t.launchpad_source),
    }
  }
  if (t.dex) {
    return {
      source: t.dex,
      name: tipDexLabel(t, t.dex),
      stage: null,
      value: null,
      color: sourceColor(t.dex),
    }
  }
  return null
}

// the same, as one line in one colour (the surfaces not on Soft yet)
export function platformTip(t: PlatformTipToken): { text: string; color: string } | null {
  const parts = platformTipParts(t)
  if (!parts) return null
  const stage = [parts.stage, parts.value].filter(Boolean).join(' ')
  return { text: stage ? `${parts.name} · ${stage}` : parts.name, color: parts.color }
}

export function PlatformTip({
  token,
  anchorRef,
  soft = false,
}: {
  token: PlatformTipToken
  /** строка/карточка-якорь: попап центрируется на её верхней кромке */
  anchorRef: RefObject<HTMLElement | null>
  /** a surface already on Soft (Trenches, Discover) gets the Soft capsule */
  soft?: boolean
}) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)

  useEffect(() => {
    const el = anchorRef.current
    if (!el) return
    const enter = () => {
      const r = el.getBoundingClientRect()
      setPos({ x: r.left + r.width / 2, y: r.top })
    }
    const leave = () => setPos(null)
    // скролл смещает якорь под fixed-попапом — прячем (capture ловит скролл
    // внутренних зон)
    const hide = () => setPos(null)
    el.addEventListener('mouseenter', enter)
    el.addEventListener('mouseleave', leave)
    window.addEventListener('scroll', hide, true)
    return () => {
      el.removeEventListener('mouseenter', enter)
      el.removeEventListener('mouseleave', leave)
      window.removeEventListener('scroll', hide, true)
    }
  }, [anchorRef])

  const parts = platformTipParts(token)
  const tip = platformTip(token)
  if (!parts || !tip || !pos) return null
  // Soft: the platform's own mark carries its colour; the words are the
  // text's, the stage quiet, gold only for a token that has migrated
  if (soft) {
    return (
      <span className={styles.tip} data-soft="" style={{ left: pos.x, top: pos.y }}>
        <span className={styles.mark}>
          <SourceLogo source={parts.source} size={13} />
        </span>
        <span className={styles.name}>{parts.name}</span>
        {parts.stage && (
          <span className={styles.stage} data-stage={parts.stage.toLowerCase()}>
            {figs(parts.stage)}
            {parts.value && <b>{parts.value}</b>}
          </span>
        )}
      </span>
    )
  }
  return (
    <span className={styles.tip} style={{ left: pos.x, top: pos.y, color: tip.color }}>
      {tip.text}
    </span>
  )
}
